import { z } from "zod";

export const RECORD_SET_COMPARISON_MAX_ROWS = 500;

/**
 * Shared scorer detail contracts. A scorer that compares a set of expected
 * records against a set of produced records (locations, coverages, losses,
 * vehicles, SOV rows…) emits `record_set_comparison`; every eval surface
 * renders it with one grid instead of a per-scorer JSON dump.
 */

export const RECORD_SET_CELL_VERDICTS = [
  "match",
  "mismatch",
  "missing",
  "extra",
  "ignored",
  "unscored",
] as const;
export type RecordSetCellVerdict = (typeof RECORD_SET_CELL_VERDICTS)[number];

export const RecordSetComparisonCellSchema = z
  .object({
    field: z.string().min(1).max(256),
    expected: z.unknown().optional(),
    produced: z.unknown().optional(),
    verdict: z.enum(RECORD_SET_CELL_VERDICTS),
    /** Partial credit for tolerance-based rules (0..1); absent means 0 or 1 per verdict. */
    credit: z.number().min(0).max(1).optional(),
    note: z.string().max(1000).optional(),
  })
  .strict();

export const RecordSetComparisonRowSchema = z
  .object({
    /** Stable row key, e.g. "1-1" for location 1 / building 1. */
    key: z.string().min(1).max(256),
    /** Index into the golden's records; null for a produced row that matched nothing. */
    expected_index: z.number().int().nonnegative().nullable(),
    /** Public id of the produced record; null for an expected row nothing matched. */
    produced_id: z.string().max(256).nullable(),
    score: z.number().min(0).max(1),
    cells: z.array(RecordSetComparisonCellSchema).max(500),
  })
  .strict();

export const RecordSetComparisonSchema = z
  .object({
    kind: z.literal("record_set_comparison"),
    label: z.string().min(1).max(256),
    match_keys: z.array(z.array(z.string().min(1).max(256)).min(1).max(16)).max(16),
    /** The rubric, so the view can say how each field was compared. */
    fields: z
      .array(
        z
          .object({
            name: z.string().min(1).max(256),
            rule: z.string().min(1).max(64),
          })
          .strict(),
      )
      .max(500),
    rows: z.array(RecordSetComparisonRowSchema).max(RECORD_SET_COMPARISON_MAX_ROWS),
    summary: z
      .object({
        expected_rows: z.number().int().nonnegative(),
        produced_rows: z.number().int().nonnegative(),
        matched_rows: z.number().int().nonnegative(),
        missing_rows: z.number().int().nonnegative(),
        extra_rows: z.number().int().nonnegative(),
        tolerated_extra_rows: z.number().int().nonnegative().optional(),
        cells_scored: z.number().int().nonnegative(),
        cells_matched: z.number().min(0),
        /** Human-readable statement of how `value` was computed. */
        formula: z.string().min(1).max(1000),
      })
      .strict(),
  })
  .strict();

export type RecordSetComparison = z.infer<typeof RecordSetComparisonSchema>;

/**
 * An agent eval result's score is a weighted mean over sections, each a
 * record-set comparison of one kind of state the run produced (custom records
 * now, tasks the agent created, final resource tags, and task links; later emails and files). One shape for every
 * kind keeps the result page a loop over sections.
 */
export const AgentEvalScoreSectionSchema = z
  .object({
    /** The golden section's key (`records:<custom_model_id>`, `tasks`, `tags`, or `linked_resources`). */
    key: z.string().min(1).max(256),
    kind: z.enum(["records", "tasks", "tags", "linked_resources"]),
    custom_model_id: z.string().min(1).max(255).optional(),
    label: z.string().min(1).max(256),
    weight: z.number().min(0),
    value: z.number().min(0).max(1),
    comparison: RecordSetComparisonSchema,
  })
  .strict();
export type AgentEvalScoreSection = z.infer<typeof AgentEvalScoreSectionSchema>;

export const AgentEvalScoreDetailSchema = z
  .object({
    kind: z.literal("sections"),
    value: z.number().min(0).max(1),
    /** Human-readable statement of how `value` was blended from the sections. */
    formula: z.string().min(1).max(1000),
    sections: z.array(AgentEvalScoreSectionSchema).min(1).max(8),
  })
  .strict();
export type AgentEvalScoreDetail = z.infer<typeof AgentEvalScoreDetailSchema>;
export type RecordSetComparisonRow = z.infer<typeof RecordSetComparisonRowSchema>;
export type RecordSetComparisonCell = z.infer<typeof RecordSetComparisonCellSchema>;

export function isRecordSetComparison(detail: unknown): detail is RecordSetComparison {
  return (
    detail !== null &&
    typeof detail === "object" &&
    (detail as { kind?: unknown }).kind === "record_set_comparison" &&
    RecordSetComparisonSchema.safeParse(detail).success
  );
}

/**
 * Scorer `detail` payloads are free-form except when they claim a shared
 * kind — then they must satisfy it, so a renderer can trust the shape.
 */
export const EvalScoreDetailSchema = z.unknown().superRefine((detail, ctx) => {
  if (
    detail !== null &&
    typeof detail === "object" &&
    (detail as { kind?: unknown }).kind === "record_set_comparison"
  ) {
    const parsed = RecordSetComparisonSchema.safeParse(detail);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) ctx.addIssue({ ...issue, path: issue.path });
    }
  }
});
